import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  setDoc,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { db } from "../../../../db/firebase";
import { useTranslation } from "react-i18next";
import {
  HiOutlineXMark,
  HiOutlineAdjustmentsHorizontal,
  HiOutlinePlus,
  HiOutlineMusicalNote,
  HiOutlinePlay,
  HiOutlineChatBubbleLeftEllipsis,
} from "react-icons/hi2";
import { FaChevronLeft, FaChevronRight, FaMoon, FaSun } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { transposeChords } from "../../../../utils/transposeChords";
import { PresentationLineChart } from "../../../../components/PresentationLineChart";
import { useAuth } from "../../../../context/AuthContext";
import { useTheme } from "../../../../context/ThemeContext";

export interface LineComment {
  id: string;
  lineIndex: number;
  linePreview: string;
  text: string;
  userId: string;
  userName: string;
  createdAt: string;
}

interface CifraSong {
  id?: number;
  title: string;
  artist: string;
  artistSlug: string;
  songSlug: string;
  artworkURL?: string | null;
  albumName?: string;
  hasVideo?: boolean;
  cifraclubUrl: string;
  imprimirUrl: string;
  imprimirUrlSimplificada?: string;
}

export interface SongWithChords {
  id: string;
  title: string;
  artist: string;
  chords?: string;
}

interface PresentationModeProps {
  songIds?: string[];
  songs?: SongWithChords[];
  eventTitle?: string;
  organizationId?: string | null;
  onClose: () => void;
}

const PresentationMode: React.FC<PresentationModeProps> = ({
  songIds = [],
  songs: songsProp,
  eventTitle,
  organizationId: organizationIdProp,
  onClose,
}) => {
  const { t } = useTranslation();
  const {
    user,
    organizationId: authOrgId,
    isAdmin,
    profile,
  } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const organizationId = organizationIdProp || authOrgId;
  const [songs, setSongs] = useState<SongWithChords[]>(songsProp || []);
  const [loading, setLoading] = useState(!songsProp && songIds.length > 0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [transposeSemitones, setTransposeSemitones] = useState(0);
  const [fontSize, setFontSize] = useState<"sm" | "md" | "lg">("md");
  const [columns, setColumns] = useState<1 | 2>(1);
  const [displayMenuOpen, setDisplayMenuOpen] = useState(false);
  const displayMenuRef = useRef<HTMLDivElement | null>(null);

  const [lineComments, setLineComments] = useState<LineComment[]>([]);
  const [activeLineIndex, setActiveLineIndex] = useState<number | null>(null);
  const [expandAllComments, setExpandAllComments] = useState(false);
  const [newCommentText, setNewCommentText] = useState("");
  const [savingComment, setSavingComment] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(
    null,
  );
  const chartScrollRef = useRef<HTMLDivElement | null>(null);

  // Add to set modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [searchSongs, setSearchSongs] = useState<CifraSong[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [artistQuery, setArtistQuery] = useState("");
  const [searchBrowseMode, setSearchBrowseMode] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [fetchingChords, setFetchingChords] = useState<string | null>(null);

  const cifraApiBase =
    process.env.REACT_APP_CIFRA_API_URL ||
    (process.env.NODE_ENV === "development" ? "http://localhost:3002" : "");

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (displayMenuRef.current && !displayMenuRef.current.contains(e.target as Node)) {
        setDisplayMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (songsProp && songsProp.length > 0) {
      setSongs(songsProp);
      setLoading(false);
      return;
    }
    const fetchSongs = async () => {
      if (songIds.length === 0) {
        setLoading(false);
        return;
      }
      try {
        const results = await Promise.all(
          songIds.map(async (songId) => {
            const songDoc = await getDoc(doc(db, "songs", songId));
            if (songDoc.exists()) {
              const data = songDoc.data();
              return {
                id: songDoc.id,
                title: data.title || "",
                artist: data.artist || "",
                chords: data.chords || data.charts || "",
              };
            }
            return null;
          })
        );
        setSongs(results.filter((s): s is SongWithChords => s !== null));
      } catch (err) {
        console.error("Error fetching songs for presentation:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSongs();
  }, [songIds, songsProp]);

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => (i <= 0 ? songs.length - 1 : i - 1));
    setTransposeSemitones(0);
    setActiveLineIndex(null);
    setExpandAllComments(false);
    setNewCommentText("");
  }, [songs.length]);

  const goNext = useCallback(() => {
    setCurrentIndex((i) => (i >= songs.length - 1 ? 0 : i + 1));
    setTransposeSemitones(0);
    setActiveLineIndex(null);
    setExpandAllComments(false);
    setNewCommentText("");
  }, [songs.length]);

  const currentSong = songs[currentIndex];

  useEffect(() => {
    if (!organizationId || !currentSong?.id) {
      setLineComments([]);
      return;
    }
    // Temp Cifra adds use synthetic ids — skip Firestore for those
    if (currentSong.id.includes("-") && currentSong.id.match(/\d{10,}$/)) {
      setLineComments([]);
      return;
    }
    const commentsRef = collection(
      db,
      "organizations",
      organizationId,
      "songs",
      currentSong.id,
      "lineComments",
    );
    const unsubscribe = onSnapshot(
      commentsRef,
      (snap) => {
        const rows = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            lineIndex: Number(data.lineIndex) || 0,
            linePreview: data.linePreview || "",
            text: data.text || "",
            userId: data.userId || "",
            userName: data.userName || "",
            createdAt: data.createdAt || "",
          } as LineComment;
        });
        rows.sort((a, b) => {
          if (a.lineIndex !== b.lineIndex) return a.lineIndex - b.lineIndex;
          return (a.createdAt || "").localeCompare(b.createdAt || "");
        });
        setLineComments(rows);
      },
      (err) => {
        console.error("lineComments subscribe failed:", err);
        setLineComments([]);
      },
    );
    return () => unsubscribe();
  }, [organizationId, currentSong?.id]);

  const isPersistableSongId = Boolean(
    currentSong?.id &&
      !(currentSong.id.includes("-") && /\d{10,}$/.test(currentSong.id)),
  );
  const commentsEnabled = Boolean(organizationId && isPersistableSongId);

  const commentsByLine = useMemo(() => {
    const groups: Record<number, LineComment[]> = {};
    lineComments.forEach((c) => {
      if (!groups[c.lineIndex]) groups[c.lineIndex] = [];
      groups[c.lineIndex].push(c);
    });
    return groups;
  }, [lineComments]);

  const openLineThread = (lineIndex: number) => {
    if (activeLineIndex === lineIndex) {
      setActiveLineIndex(null);
      setNewCommentText("");
      return;
    }
    setActiveLineIndex(lineIndex);
    setNewCommentText("");
    requestAnimationFrame(() => {
      const el = chartScrollRef.current?.querySelector(
        `[data-line-index="${lineIndex}"]`,
      );
      el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  const closeLineThread = () => {
    setActiveLineIndex(null);
    setNewCommentText("");
  };

  const displayName =
    [profile?.firstName, profile?.lastName].filter(Boolean).join(" ").trim() ||
    user?.email ||
    "Member";

  const handleAddLineComment = async () => {
    if (
      !organizationId ||
      !currentSong?.id ||
      activeLineIndex === null ||
      !user ||
      !newCommentText.trim()
    ) {
      return;
    }
    setSavingComment(true);
    try {
      const rawLine =
        (currentSong.chords || "").split("\n")[activeLineIndex] || "";
      const linePreview = rawLine.trim().slice(0, 80) || `Line ${activeLineIndex + 1}`;
      const id = uuidv4();
      const payload: LineComment = {
        id,
        lineIndex: activeLineIndex,
        linePreview,
        text: newCommentText.trim(),
        userId: user.uid,
        userName: displayName,
        createdAt: new Date().toISOString(),
      };
      await setDoc(
        doc(
          db,
          "organizations",
          organizationId,
          "songs",
          currentSong.id,
          "lineComments",
          id,
        ),
        payload,
      );
      setNewCommentText("");
    } catch (err) {
      console.error("Failed to add line comment:", err);
    } finally {
      setSavingComment(false);
    }
  };

  const handleDeleteLineComment = async (commentId: string) => {
    if (!organizationId || !currentSong?.id) return;
    const comment = lineComments.find((c) => c.id === commentId);
    if (!comment) return;
    if (comment.userId !== user?.uid && !isAdmin) return;
    setDeletingCommentId(comment.id);
    try {
      await deleteDoc(
        doc(
          db,
          "organizations",
          organizationId,
          "songs",
          currentSong.id,
          "lineComments",
          comment.id,
        ),
      );
    } catch (err) {
      console.error("Failed to delete line comment:", err);
    } finally {
      setDeletingCommentId(null);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (activeLineIndex !== null) {
          closeLineThread();
          return;
        }
        if (expandAllComments) {
          setExpandAllComments(false);
          return;
        }
        onClose();
        return;
      }
      if (activeLineIndex !== null) return;
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, goPrev, goNext, activeLineIndex, expandAllComments]);

  const handleTranspose = (delta: number) => {
    setTransposeSemitones((v) => {
      const newVal = v + delta;
      if (newVal < -11 || newVal > 11) return v;
      return newVal;
    });
  };

  const handleSearchCifra = async (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setSearchLoading(true);
    setSearchError("");
    setSearchSongs([]);
    try {
      const url = cifraApiBase
        ? `${cifraApiBase}/cifraSearch`
        : `/.netlify/functions/cifraSearch`;
      const res = await axios.get(url, { params: { q, limit: 30 } });
      setSearchSongs(res.data.songs || []);
      if (!res.data.songs?.length) {
        setSearchError(t("noSongsFound") || "No songs found");
      }
    } catch (err: any) {
      setSearchError(err.response?.data?.error || "Search failed");
      setSearchSongs([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleBrowseArtistCifra = async (e: React.FormEvent) => {
    e.preventDefault();
    const artist = artistQuery.trim();
    if (!artist) return;
    setSearchLoading(true);
    setSearchError("");
    setSearchSongs([]);
    try {
      const url = cifraApiBase
        ? `${cifraApiBase}/cifraArtistSongs`
        : `/.netlify/functions/cifraArtistSongs`;
      const res = await axios.get(url, {
        params: { artist, order: "alphabetical" },
      });
      setSearchSongs(res.data.songs || []);
      if (!res.data.songs?.length) {
        setSearchError(t("noSongsFound") || "No songs found");
      }
    } catch (err: any) {
      setSearchError(err.response?.data?.error || "Failed to load artist songs");
      setSearchSongs([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddToSet = async (
    song: CifraSong,
    version: "principal" | "simplificada"
  ) => {
    const chordsUrlToUse =
      version === "simplificada" && song.imprimirUrlSimplificada
        ? song.imprimirUrlSimplificada
        : song.imprimirUrl;
    setFetchingChords(`${song.songSlug}-${version}`);
    try {
      const base = cifraApiBase || "";
      const chordsUrl = base
        ? `${base}/fetchChords`
        : `/.netlify/functions/fetchChords`;
      const chordsRes = await axios.get(chordsUrl, {
        params: { url: chordsUrlToUse },
      });
      const charts = chordsRes.data?.chords || "";
      const newSong: SongWithChords = {
        id: `${song.songSlug}-${version}-${Date.now()}`,
        title: song.title,
        artist: song.artist,
        chords: charts,
      };
      setSongs((prev) => [...prev, newSong]);
      setCurrentIndex(songs.length);
      setAddModalOpen(false);
    } catch (err: any) {
      setSearchError(err.response?.data?.error || "Failed to load chords");
    } finally {
      setFetchingChords(null);
    }
  };

  const songKey = (s: CifraSong) => `${s.artistSlug}-${s.songSlug}`;

  const fontSizeClass =
    fontSize === "sm"
      ? "text-sm sm:text-base md:text-lg lg:text-xl"
      : fontSize === "md"
        ? "text-base sm:text-lg md:text-xl lg:text-2xl"
        : "text-lg sm:text-xl md:text-2xl lg:text-3xl";

  const iconBtn =
    "p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-black/5 dark:text-white/60 dark:hover:text-white dark:hover:bg-white/10 transition-colors";
  const navBtn =
    "p-2 sm:p-3 rounded-full text-gray-500 hover:text-gray-900 hover:bg-black/5 dark:text-white/60 dark:hover:text-white dark:hover:bg-white/10 transition-colors shrink-0 touch-manipulation";

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-800 dark:text-white text-xl">{t("loading")}</div>
      </div>
    );
  }

  if (songs.length === 0) {
    return (
      <div className="fixed inset-0 z-50 bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-8">
        <p className="text-gray-600 dark:text-white/80 text-lg mb-6">
          {t("noSongsInPlaylist") || "No songs in this event"}
        </p>
        <button
          onClick={onClose}
          className="px-6 py-3 bg-black/5 hover:bg-black/10 text-gray-800 dark:bg-white/20 dark:hover:bg-white/30 dark:text-white rounded-xl transition-colors"
        >
          {t("close")}
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-gray-50 dark:bg-gray-900 flex flex-col overflow-hidden overflow-x-hidden">
      {/* Header with prev/next at top */}
      <div className="flex justify-between items-center px-2 sm:px-6 py-3 sm:py-4 bg-white/80 dark:bg-black/30 border-b border-gray-200/80 dark:border-transparent backdrop-blur-sm shrink-0 flex-wrap gap-2 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-4">
          <button
            onClick={goPrev}
            className={navBtn}
            aria-label={t("previous") || "Previous"}
          >
            <FaChevronLeft className="w-5 h-5 sm:w-8 sm:h-8" />
          </button>
          <span className="text-gray-500 dark:text-white/60 text-sm">
            {currentIndex + 1} / {songs.length}
          </span>
          <button
            onClick={goNext}
            className={navBtn}
            aria-label={t("next") || "Next"}
          >
            <FaChevronRight className="w-5 h-5 sm:w-8 sm:h-8" />
          </button>
          {eventTitle && (
            <span className="text-gray-400 dark:text-white/40 text-sm hidden sm:inline">
              • {eventTitle}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {commentsEnabled && (
            <button
              type="button"
              onClick={() => setExpandAllComments((v) => !v)}
              className={`p-2 rounded-lg transition-colors flex items-center gap-1.5 ${
                expandAllComments
                  ? "text-violet-700 bg-violet-500/15 dark:text-violet-200 dark:bg-violet-500/20"
                  : iconBtn
              }`}
              aria-label={t("openAllComments") || "Open all comments"}
            >
              <HiOutlineChatBubbleLeftEllipsis className="w-5 h-5 shrink-0" />
              <span className="text-sm hidden sm:inline">
                {expandAllComments
                  ? t("hideAllComments") || "Hide comments"
                  : t("openAllComments") || "Comments"}
                {lineComments.length > 0 ? ` (${lineComments.length})` : ""}
              </span>
              {lineComments.length > 0 && (
                <span className="sm:hidden text-xs font-semibold text-violet-600 dark:text-violet-300">
                  {lineComments.length}
                </span>
              )}
            </button>
          )}
          <button
            onClick={() => setAddModalOpen(true)}
            className={`${iconBtn} flex items-center gap-1.5`}
            aria-label={t("addSong") || "Add Song"}
          >
            <HiOutlinePlus className="w-5 h-5 shrink-0" />
            <span className="text-sm">{t("addSong") || "Add Song"}</span>
          </button>
          {/* Display options */}
          <div className="relative" ref={displayMenuRef}>
            <button
              onClick={() => setDisplayMenuOpen((o) => !o)}
              className={iconBtn}
              aria-label={t("displayOptions") || "Display options"}
            >
              <HiOutlineAdjustmentsHorizontal className="w-6 h-6" />
            </button>
            {displayMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-52 rounded-xl shadow-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 py-2 z-30">
                <div className="px-3 py-1.5 text-xs font-medium text-gray-400 dark:text-white/50 uppercase tracking-wider">
                  {t("fontSize") || "Font size"}
                </div>
                <div className="flex gap-1 px-2">
                  {(["sm", "md", "lg"] as const).map((size) => (
                    <button
                      key={size}
                      onClick={() => setFontSize(size)}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                        fontSize === size
                          ? "bg-teal-500/20 text-teal-700 dark:bg-teal-500/30 dark:text-teal-400"
                          : "text-gray-600 hover:bg-gray-100 dark:text-white/70 dark:hover:bg-white/10 dark:hover:text-white"
                      }`}
                    >
                      {t(`fontSize${size.charAt(0).toUpperCase() + size.slice(1)}`) || size}
                    </button>
                  ))}
                </div>
                <div className="px-3 py-1.5 mt-2 text-xs font-medium text-gray-400 dark:text-white/50 uppercase tracking-wider">
                  {t("columns") || "Columns"}
                </div>
                <div className="flex gap-1 px-2">
                  {([1, 2] as const).map((col) => (
                    <button
                      key={col}
                      onClick={() => setColumns(col)}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                        columns === col
                          ? "bg-teal-500/20 text-teal-700 dark:bg-teal-500/30 dark:text-teal-400"
                          : "text-gray-600 hover:bg-gray-100 dark:text-white/70 dark:hover:bg-white/10 dark:hover:text-white"
                      }`}
                    >
                      {col === 1 ? (t("oneColumn") || "1") : (t("twoColumns") || "2")}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          {currentSong.chords && (
            <div className="flex items-center gap-0.5 sm:gap-1 bg-black/5 dark:bg-white/10 rounded-lg px-1.5 sm:px-2 py-1 sm:py-1.5">
              <span className="text-xs text-gray-500 dark:text-white/60 mr-1">
                {t("transpose")}:
              </span>
              <button
                type="button"
                onClick={() => handleTranspose(-1)}
                disabled={transposeSemitones <= -11}
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-black/5 dark:bg-white/10 text-gray-800 dark:text-white hover:bg-black/10 dark:hover:bg-white/20 disabled:opacity-40 font-medium text-xs sm:text-sm transition-colors touch-manipulation"
              >
                −
              </button>
              <span className="min-w-[1.75rem] sm:min-w-[2.25rem] text-center text-xs sm:text-sm font-semibold text-gray-800 dark:text-white">
                {transposeSemitones >= 0 ? `+${transposeSemitones}` : transposeSemitones}
              </span>
              <button
                type="button"
                onClick={() => handleTranspose(1)}
                disabled={transposeSemitones >= 11}
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-black/5 dark:bg-white/10 text-gray-800 dark:text-white hover:bg-black/10 dark:hover:bg-white/20 disabled:opacity-40 font-medium text-xs sm:text-sm transition-colors touch-manipulation"
              >
                +
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={toggleTheme}
            className={iconBtn}
            title={theme === "dark" ? t("lightMode") : t("darkMode")}
            aria-label={theme === "dark" ? t("lightMode") : t("darkMode")}
          >
            {theme === "dark" ? (
              <FaSun className="w-5 h-5" />
            ) : (
              <FaMoon className="w-5 h-5" />
            )}
          </button>
          <button
            onClick={onClose}
            className={iconBtn}
            aria-label={t("close")}
          >
            <HiOutlineXMark className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Carousel content - full width on mobile */}
      <div className="flex-1 min-h-0 flex items-stretch justify-center px-0 sm:px-8">
        <div
          ref={chartScrollRef}
          className="flex-1 min-w-0 min-h-0 w-full flex flex-col items-center text-center px-1 sm:px-4 overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-black/5 dark:[&::-webkit-scrollbar-track]:bg-white/10 [&::-webkit-scrollbar-thumb]:bg-black/20 dark:[&::-webkit-scrollbar-thumb]:bg-white/30 [&::-webkit-scrollbar-thumb]:rounded-full"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSong.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="w-full flex flex-col items-center py-2 sm:py-4"
            >
              <h2 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                {currentSong.title}
              </h2>
              <p className="text-gray-500 dark:text-white/60 text-base sm:text-lg mb-4 sm:mb-6">
                {currentSong.artist}
              </p>
              <div
                className={`w-full max-w-full md:max-w-[297mm] min-h-0 md:min-h-[210mm] text-left overflow-x-hidden mx-auto gap-x-4 md:gap-x-8 py-4 sm:py-6 px-2 sm:px-8 bg-white dark:bg-white/5 border border-gray-200/80 dark:border-transparent rounded-lg shadow-sm dark:shadow-none ${
                  columns === 1 || commentsEnabled
                    ? "columns-1"
                    : "columns-1 md:columns-2"
                }`}
              >
                {currentSong.chords ? (
                  <PresentationLineChart
                    rawText={currentSong.chords}
                    displayText={transposeChords(
                      currentSong.chords,
                      transposeSemitones,
                    )}
                    className={`text-gray-800 dark:text-white ${fontSizeClass}`}
                    chordClassName="text-teal-600 dark:text-teal-400"
                    commentsEnabled={commentsEnabled}
                    commentsByLine={commentsByLine}
                    activeLineIndex={activeLineIndex}
                    expandAll={expandAllComments}
                    currentUserId={user?.uid}
                    canDeleteAny={isAdmin}
                    newCommentText={newCommentText}
                    savingComment={savingComment}
                    deletingCommentId={deletingCommentId}
                    addLabel={t("addLineComment") || "Add comment"}
                    placeholder={
                      t("addLineCommentPlaceholder") ||
                      "Add a note for this line…"
                    }
                    savingLabel={t("saving") || "Saving…"}
                    onLineBubbleClick={openLineThread}
                    onCloseLine={closeLineThread}
                    onNewCommentChange={setNewCommentText}
                    onAddComment={handleAddLineComment}
                    onDeleteComment={handleDeleteLineComment}
                  />
                ) : (
                  <p className="text-gray-500 dark:text-white/60 text-lg">
                    {t("noChordsYet") || "No chords for this song."}
                  </p>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Dots indicator */}
      <div className="flex justify-center gap-1.5 sm:gap-2 py-3 sm:py-4 shrink-0">
        {songs.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentIndex(i)}
            className={`w-2 h-2 rounded-full transition-colors ${
              i === currentIndex
                ? "bg-gray-800 dark:bg-white"
                : "bg-gray-300 hover:bg-gray-400 dark:bg-white/30 dark:hover:bg-white/50"
            }`}
            aria-label={`Go to song ${i + 1}`}
          />
        ))}
      </div>

      {/* Add to set modal */}
      {addModalOpen && (
        <div className="fixed inset-0 z-[60] bg-black/50 dark:bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/10 shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t("addSong") || "Add Song"}
              </h3>
              <button
                onClick={() => {
                  setAddModalOpen(false);
                  setSearchError("");
                  setSearchSongs([]);
                }}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-black/5 dark:text-white/60 dark:hover:text-white dark:hover:bg-white/10"
              >
                <HiOutlineXMark className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 min-h-0">
              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setSearchBrowseMode(false)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !searchBrowseMode
                      ? "bg-[#5932EA] text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-white/70 dark:hover:text-white"
                  }`}
                >
                  Search
                </button>
                <button
                  type="button"
                  onClick={() => setSearchBrowseMode(true)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    searchBrowseMode
                      ? "bg-[#5932EA] text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-white/70 dark:hover:text-white"
                  }`}
                >
                  Browse by artist
                </button>
              </div>
              {searchBrowseMode ? (
                <form onSubmit={handleBrowseArtistCifra} className="mb-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder={t("artist") || "Artist name"}
                      value={artistQuery}
                      onChange={(e) => setArtistQuery(e.target.value)}
                      className="flex-1 px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/20 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/40 focus:ring-2 focus:ring-[#5932EA]/50"
                    />
                    <button
                      type="submit"
                      disabled={searchLoading || !artistQuery.trim()}
                      className="px-4 py-2.5 rounded-xl bg-[#5932EA] text-white font-medium disabled:opacity-50"
                    >
                      {searchLoading ? "…" : "Load"}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleSearchCifra} className="mb-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder={t("searchByNameOrArtist") || "Search by artist or song..."}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/20 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/40 focus:ring-2 focus:ring-[#5932EA]/50"
                    />
                    <button
                      type="submit"
                      disabled={searchLoading || !searchQuery.trim()}
                      className="px-4 py-2.5 rounded-xl bg-[#5932EA] text-white font-medium disabled:opacity-50"
                    >
                      {searchLoading ? "…" : t("search") || "Search"}
                    </button>
                  </div>
                </form>
              )}
              {searchError && (
                <p className="text-red-500 dark:text-red-400 text-sm mb-4">{searchError}</p>
              )}
              <ul className="space-y-2 max-h-[40vh] overflow-y-auto">
                {searchSongs.map((song) => (
                  <li
                    key={songKey(song)}
                    className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-200 dark:bg-white/10 shrink-0 flex items-center justify-center">
                      {song.artworkURL ? (
                        <img
                          src={song.artworkURL}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <HiOutlineMusicalNote className="w-5 h-5 text-gray-400 dark:text-white/40" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 dark:text-white truncate">{song.title}</p>
                      <p className="text-sm text-gray-500 dark:text-white/60 truncate">{song.artist}</p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button
                        onClick={() => handleAddToSet(song, "principal")}
                        disabled={fetchingChords !== null}
                        className="px-2 py-1.5 rounded-lg text-xs font-semibold bg-[#5932EA] text-white hover:bg-[#4a28c7] disabled:opacity-50 flex items-center gap-1"
                      >
                        {fetchingChords === `${song.songSlug}-principal` ? (
                          <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <HiOutlinePlay className="w-3 h-3" />
                        )}
                        Version 1
                      </button>
                      <button
                        onClick={() => handleAddToSet(song, "simplificada")}
                        disabled={fetchingChords !== null}
                        className="px-2 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-white/30 dark:text-white/80 dark:hover:bg-white/10 disabled:opacity-50 flex items-center gap-1"
                      >
                        {fetchingChords === `${song.songSlug}-simplificada` ? (
                          <span className="w-3 h-3 border-2 border-gray-300 border-t-gray-700 dark:border-white/30 dark:border-t-white rounded-full animate-spin" />
                        ) : (
                          <HiOutlinePlay className="w-3 h-3" />
                        )}
                        Version 2
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
              {searchSongs.length === 0 && !searchLoading && !searchError && (
                <p className="text-gray-400 dark:text-white/50 text-sm text-center py-8">
                  {t("searchByNameOrArtist") || "Search by artist or song to add to the set"}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresentationMode;
